/**
 * Meeting Outcome Modal Component
 */
import { X, Trophy, Mail } from "lucide-react";
import { MEETING_OUTCOMES, NEXT_STEP_OPTIONS, LOST_REASONS } from "../hooks/useMeetingOutcome";
import { formatDateTime } from "../../../utils/dateTime";
import VoiceNoteButton from "../../../components/common/VoiceNoteButton";

/**
 * Generate mailto link with company data and notes
 */
const generateMailtoLink = (company, notes, backofficeEmail) => {
  const recipient = backofficeEmail || "backoffice@tvrtka.hr";
  const subject = encodeURIComponent(`Zahtjev za ponudu - ${company?.name || 'Nepoznata firma'}`);
  
  const bodyLines = [
    `Poštovani,`,
    ``,
    `Molim vas za izradu ponude za sljedeću firmu:`,
    ``,
    `PODACI O FIRMI:`,
    `- Naziv: ${company?.name || 'N/A'}`,
    `- OIB: ${company?.oib || 'N/A'}`,
    `- Adresa: ${company?.address || 'N/A'}`,
    `- Grad: ${company?.city || 'N/A'}`,
    `- Direktor: ${company?.director_name || 'N/A'}`,
    `- Telefon: ${company?.phone || 'N/A'}`,
    `- Email: ${company?.email || 'N/A'}`,
    `- Industrija: ${company?.industry || 'N/A'}`,
    ``
  ];
  
  if (notes && notes.trim()) {
    bodyLines.push(`NAPOMENE SA SASTANKA:`);
    bodyLines.push(notes);
    bodyLines.push(``);
  }
  
  bodyLines.push(`Hvala!`);
  
  const body = encodeURIComponent(bodyLines.join('\n'));
  
  return `mailto:${recipient}?subject=${subject}&body=${body}`;
};

export const MeetingOutcomeModal = ({
  show,
  onClose,
  selectedMeeting,
  meetingOutcome,
  setMeetingOutcome,
  outcomeNote,
  setOutcomeNote,
  nextStep,
  setNextStep,
  nextActionDate,
  setNextActionDate,
  rescheduledDateTime,
  setRescheduledDateTime,
  lostReason,
  setLostReason,
  savingOutcome,
  onSave,
  company,
  backofficeEmail
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-[#1B3F8F]" />
            <h3 className="text-lg font-semibold text-gray-900">Ishod sastanka</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        {/* Meeting info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-sm text-gray-600">
            Sastanak zakazan: <span className="font-medium">{formatDateTime(selectedMeeting?.meeting_datetime)}</span>
          </p>
        </div>
        
        {/* Outcome selection */}
        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-gray-700">Ishod sastanka</label>
          <div className="grid grid-cols-2 gap-2">
            {MEETING_OUTCOMES.map((outcome) => (
              <button
                key={outcome.value}
                onClick={() => setMeetingOutcome(outcome.value)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${
                  meetingOutcome === outcome.value
                    ? "border-[#1B3F8F] bg-[#1B3F8F]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <span className={`font-medium ${outcome.color}`}>{outcome.label}</span>
                {outcome.description && (
                  <p className="text-xs text-gray-500 mt-1">{outcome.description}</p>
                )}
              </button>
            ))}
          </div>
        </div>
        
        {/* Conditional fields based on outcome */}
        {meetingOutcome === "HELD" && (
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sljedeći korak</label>
              <select
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
              >
                <option value="">Odaberi sljedeći korak...</option>
                {NEXT_STEP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {nextStep === "NEW_MEETING" ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum i vrijeme novog sastanka</label>
                <input
                  type="datetime-local"
                  value={rescheduledDateTime}
                  onChange={(e) => setRescheduledDateTime(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                />
              </div>
            ) : nextStep === "REQUEST_OFFER_BACKOFFICE" ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Datum sljedeće akcije</label>
                  <input
                    type="date"
                    value={nextActionDate}
                    onChange={(e) => setNextActionDate(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                  />
                </div>
                
                {/* Send email to back office button */}
                <a
                  href={generateMailtoLink(company, outcomeNote, backofficeEmail)}
                  data-testid="send-backoffice-email-btn"
                  className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-2 shadow-md"
                >
                  <Mail size={18} />
                  Pošalji zahtjev za ponudu
                </a>
                <p className="text-xs text-gray-500 text-center">
                  Klikni za otvaranje email klijenta s unaprijed popunjenim podacima
                </p>
              </>
            ) : nextStep && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Datum sljedeće akcije</label>
                <input
                  type="date"
                  value={nextActionDate}
                  onChange={(e) => setNextActionDate(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                />
              </div>
            )}
          </div>
        )}
        
        {meetingOutcome === "RESCHEDULED" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Novi datum i vrijeme sastanka</label>
            <input
              type="datetime-local"
              value={rescheduledDateTime}
              onChange={(e) => setRescheduledDateTime(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
            />
          </div>
        )}
        
        {meetingOutcome === "LOST" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Razlog gubitka</label>
            <select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
            >
              <option value="">Odaberi razlog...</option>
              {LOST_REASONS.map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Note field */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Napomena (opcionalno)</label>
            <VoiceNoteButton onTranscript={(text) => setOutcomeNote(prev => prev ? prev + " " + text : text)} />
          </div>
          <textarea
            value={outcomeNote}
            onChange={(e) => setOutcomeNote(e.target.value)}
            rows={3}
            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F] resize-none"
            placeholder="Dodaj napomenu o sastanku..."
          />
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
          >
            Odustani
          </button>
          <button
            onClick={onSave}
            disabled={savingOutcome || !meetingOutcome}
            className="flex-1 py-3 px-4 bg-[#1B3F8F] text-white rounded-xl font-medium hover:bg-[#15327a] disabled:opacity-50"
          >
            {savingOutcome ? "Spremam..." : "Spremi ishod"}
          </button>
        </div>
      </div>
    </div>
  );
};
